import { useState, useEffect, useMemo, useContext, useRef } from 'react';
import settingsParams, {
  IManageCategories,
} from '../components/FaqOoi/settingsParams';
import constants from '../constants';
import type { Category } from '@wix/ambassador-faq-category-v2-category/types';
import { ISettingsContextValue, IWixStyleParams } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { AppContext } from '../components/FaqOoi/Widget/SettingsProvider/SettingsProvider';
import stylesParams from '../components/FaqOoi/stylesParams';
import { useLayoutType } from './useLayoutType';
import { CategoriesPosition } from '../enums';

export const useCategories = (validLists: Category[]) => {
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();
  const { isEditor } = useEnvironment();
  const {
    setSelectedCategory,
    selectedCategory,
    listCategoriesResponse,
    appAPI: { getListCategories },
    isEditor3,
  } = useContext(AppContext);
  const { categoriesPosition, displayContent } = useLayoutType(isEditor3);
  const showCategoriesStyleParam =
    styles.get?.(stylesParams.showFAQCategories) ?? true;
  const manageCategories: IManageCategories = settings.get(
    settingsParams.manageCategories,
  );
  const showAll = manageCategories?.showAll;
  const categoriesJSON = JSON.stringify(manageCategories?.categories);
  const categories = useMemo(() => {
    try {
      return JSON.parse(categoriesJSON);
    } catch (error) {
      console.error('Failed to parse categories JSON:', error);
      return [];
    }
  }, [categoriesJSON]);
  const splitIntoCategories =
    displayContent === constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES &&
    showCategoriesStyleParam;
  const showOneListOfQuestions = !splitIntoCategories;
  const sideCategoriesPosition = categoriesPosition === CategoriesPosition.SIDE;
  const topCategoriesPosition = categoriesPosition === CategoriesPosition.TOP;

  // Keep a ref to the latest selectedCategory so effects can read it without
  // adding it as a dependency (which would cause unintended re-runs).
  const selectedCategoryRef = useRef(selectedCategory);
  selectedCategoryRef.current = selectedCategory;

  // State for categories list
  const [listCategories, setListCategories] = useState<Category[]>(
    validLists || [],
  );
  const filteredCategoriesIds = listCategories.map((category) => category.id);

  // Manage active tab
  const [activeTab, setActiveTab] = useState(
    typeof selectedCategory === 'string'
      ? filteredCategoriesIds.indexOf(selectedCategory)
      : 0,
  );

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      await getListCategories();
    };

    if (isEditor) {
      fetchCategories();
    }
  }, [getListCategories, isEditor]);

  useEffect(() => {
    if (listCategoriesResponse) {
      const { categories: categoriesList } = listCategoriesResponse ?? {};

      let firstId: string | undefined | null;
      const localValidLists = categoriesList?.length
        ? categoriesList?.filter((category: { id: string }) => {
            const categoryId = category?.id;
            if (!categoryId) {
              return false;
            }
            if (showAll || categories?.includes(categoryId)) {
              if (firstId === undefined) {
                firstId = categoryId;
              }
              return true;
            }
            return false;
          })
        : [];

      setListCategories(localValidLists ?? []);

      if (showOneListOfQuestions) {
        const validCategoryIds = localValidLists
          ?.filter((category: Category) => category?.id)
          .map((category: Category) => category?.id);

        setSelectedCategory?.(validCategoryIds);
      } else if (firstId) {
        // Don't overwrite selectedCategory when it already points to a valid
        // category — this happens when arriving via a share link where the
        // question's category was pre-selected by the controller.
        const validIds = (localValidLists ?? [])
          .map((c: Category) => c?.id)
          .filter(Boolean) as string[];
        const currentIsValid =
          typeof selectedCategoryRef.current === 'string' &&
          validIds.includes(selectedCategoryRef.current);
        if (!currentIsValid) {
          setSelectedCategory?.(firstId);
        }
      }
    }
  }, [
    categories,
    listCategoriesResponse,
    setSelectedCategory,
    showAll,
    showOneListOfQuestions,
  ]);
  // Handle tab selection updates
  useEffect(() => {
    if (setSelectedCategory && !showOneListOfQuestions) {
      const categoryId = listCategories[activeTab]?.id;
      if (categoryId) {
        setSelectedCategory?.(categoryId);
      }
    }
  }, [activeTab, listCategories, setSelectedCategory, showOneListOfQuestions]);

  // Filter categories to render
  const categoriesToRender = useMemo(() => {
    if (showAll) {
      return listCategories;
    }
    return categories
      ? listCategories?.filter((c) => categories.includes(c.id!))
      : [];
  }, [showAll, categories, listCategories]);

  // Adjust active tab if necessary
  const adjustedActiveTab = useMemo(() => {
    return categoriesToRender.length > 0 &&
      activeTab >= categoriesToRender.length
      ? 0
      : activeTab;
  }, [activeTab, categoriesToRender]);

  return {
    categoriesToRender,
    activeTab: adjustedActiveTab,
    setActiveTab,
    splitIntoCategories,
    topCategoriesPosition,
    sideCategoriesPosition,
  };
};
